import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { MapContainer, TileLayer, Marker, Popup } from "react-leaflet";
import L from "leaflet";
import { motion } from "framer-motion";
import { Package, Truck, Clock, ShoppingBag } from "lucide-react";
import { LiveStatusDot } from "../components/LiveStatusDot";

const LOGO_URL =
  "https://customer-assets.emergentagent.com/job_c129d2ac-2209-4214-9ac4-af6d02f0e7d1/artifacts/e10f2m7m_hf_20260429_103436_b903d0ed-1980-49ff-95c4-cf93c75881ec.png";

const icon = new L.Icon({
  iconUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon.png",
  iconRetinaUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-icon-2x.png",
  shadowUrl: "https://unpkg.com/leaflet@1.9.4/dist/images/marker-shadow.png",
  iconSize: [25, 41],
  iconAnchor: [12, 41],
  popupAnchor: [1, -34],
});

export default function PublicTrack() {
  const { token } = useParams();
  const [data, setData] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    const base = process.env.REACT_APP_BACKEND_URL || "";
    fetch(`${base}/api/public/track/${token}`)
      .then(async (r) => {
        if (r.status === 404) throw new Error("This tracking link has expired or is invalid.");
        if (!r.ok) throw new Error("Could not load tracking.");
        return r.json();
      })
      .then(setData)
      .catch((e) => setError(e.message));

    // Subscribe to SSE for this token's rikshaw
    const es = new EventSource(`${base}/api/public/track/${token}/rikshaw-stream`);
    es.onmessage = (evt) => {
      try {
        const m = JSON.parse(evt.data);
        setData((d) => {
          if (!d?.rikshaw || d.rikshaw.id !== m.rikshaw_id) return d;
          return {
            ...d,
            rikshaw: {
              ...d.rikshaw,
              lat: m.lat,
              lng: m.lng,
              status: m.status,
              last_ping_at: m.last_ping_at,
            },
          };
        });
      } catch {
        // ignore malformed frames
      }
    };
    es.onerror = () => es.close();
    return () => es.close();
  }, [token]);

  if (error) {
    return (
      <PublicShell>
        <div className="max-w-md mx-auto px-6 py-20 text-center" data-testid="public-track-error">
          <img src={LOGO_URL} alt="JOW" className="h-14 w-14 mx-auto" />
          <h1 className="font-display text-3xl text-jow-navy mt-6">Link expired</h1>
          <p className="text-sm text-jow-navy/60 mt-2">
            {error} Ask the buyer for a fresh one, or browse pieces we're bringing to your neighborhood.
          </p>
          <Link to="/" className="btn-primary-jow mt-6 inline-flex" data-testid="back-home-btn">
            Visit JewelleryOnWheels
          </Link>
        </div>
      </PublicShell>
    );
  }

  if (!data) {
    return (
      <PublicShell>
        <div className="max-w-md mx-auto px-6 py-20 text-center label-overline text-jow-navy/50">
          Loading…
        </div>
      </PublicShell>
    );
  }

  const rikshaw = data.rikshaw;
  const center = rikshaw
    ? [rikshaw.lat || 22.97, rikshaw.lng || 78.65]
    : [22.97, 78.65];

  return (
    <PublicShell>
      <main
        className="max-w-4xl mx-auto px-4 sm:px-6 py-10 md:py-14"
        data-testid="public-track"
      >
        <motion.div initial={{ opacity: 0, y: 8 }} animate={{ opacity: 1, y: 0 }}>
          <span className="label-overline text-jow-gold">Live tracking</span>
          <h1 className="font-display text-3xl md:text-5xl text-jow-navy mt-2">
            {data.order_no_masked}
          </h1>
          <p className="text-sm text-jow-navy/60 mt-1">
            Status:{" "}
            <strong className="text-jow-navy capitalize">
              {(data.status || "").replaceAll("_", " ")}
            </strong>
            {data.city && <span className="text-jow-navy/50"> · {data.city}</span>}
          </p>
          {data.eta_text && (
            <p
              className="mt-3 inline-flex items-center gap-2 bg-jow-gold/15 border border-jow-gold px-3 py-1.5 text-sm text-jow-navy"
              data-testid="public-eta"
            >
              <Clock size={14} /> {data.eta_text}
            </p>
          )}
        </motion.div>

        <div
          className="mt-6 bg-white border border-jow-gold/40 overflow-hidden"
          style={{ height: 380 }}
          data-testid="public-map"
        >
          <MapContainer
            center={center}
            zoom={rikshaw ? 12 : 5}
            scrollWheelZoom
            style={{ height: "100%", width: "100%" }}
          >
            <TileLayer
              attribution='&copy; OpenStreetMap'
              url="https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png"
            />
            {rikshaw && rikshaw.lat && (
              <Marker position={[rikshaw.lat, rikshaw.lng]} icon={icon}>
                <Popup>
                  <strong>{rikshaw.code}</strong>
                  {rikshaw.driver_name && (
                    <>
                      <br />
                      {rikshaw.driver_name}
                    </>
                  )}
                </Popup>
              </Marker>
            )}
          </MapContainer>
        </div>

        <div className="grid md:grid-cols-2 gap-4 mt-5">
          {/* Rikshaw card */}
          <section
            className="bg-white border border-jow-navy/10 p-5"
            data-testid="public-rikshaw"
          >
            <p className="label-overline text-jow-navy/55 mb-2">Rikshaw</p>
            {rikshaw ? (
              <>
                <p className="font-display text-xl text-jow-navy">{rikshaw.code}</p>
                {rikshaw.driver_name && (
                  <p className="text-sm text-jow-navy/65 mt-1">{rikshaw.driver_name}</p>
                )}
                <div className="mt-3">
                  <LiveStatusDot
                    lastPingAt={rikshaw.last_ping_at}
                    testid="public-live-dot"
                  />
                </div>
              </>
            ) : (
              <p className="text-sm text-jow-navy/55">
                <Truck size={14} className="inline text-jow-gold mr-1" /> Not yet on a rikshaw.
              </p>
            )}
          </section>

          {/* Items */}
          <section className="bg-white border border-jow-navy/10 p-5">
            <p className="label-overline text-jow-navy/55 mb-3">In the pouch</p>
            <ul className="space-y-2">
              {data.items?.map((it, i) => (
                <li key={i} className="flex items-center gap-3 text-sm">
                  {it.image && (
                    <img src={it.image} alt="" className="w-10 h-10 object-cover rounded" />
                  )}
                  <div className="flex-1">
                    <p className="text-jow-navy">{it.name}</p>
                    <p className="text-[10px] text-jow-navy/50 uppercase tracking-widest">
                      Qty {it.qty}
                    </p>
                  </div>
                </li>
              ))}
            </ul>
          </section>
        </div>

        {/* Timeline */}
        <section
          className="mt-5 bg-white border border-jow-navy/10 p-5"
          data-testid="public-timeline"
        >
          <p className="label-overline text-jow-navy/55 mb-3">Journey</p>
          <ol className="space-y-3">
            {(data.timeline || []).map((t, i) => (
              <li key={i} className="flex gap-3 text-sm">
                <Package size={13} className="text-jow-gold mt-1" />
                <div className="flex-1">
                  <p className="text-jow-navy capitalize">
                    {(t.status || "").replaceAll("_", " ")}
                  </p>
                  <p className="text-[10px] text-jow-navy/50">
                    {new Date(t.at).toLocaleString()}
                    {t.note ? ` · ${t.note}` : ""}
                  </p>
                </div>
              </li>
            ))}
          </ol>
        </section>

        {/* CTA */}
        <div
          className="mt-8 bg-jow-navy text-jow-cream p-6 flex flex-col sm:flex-row items-center gap-4"
          data-testid="public-cta"
        >
          <ShoppingBag size={20} className="text-jow-gold" />
          <div className="flex-1 text-center sm:text-left">
            <p className="font-display text-lg">Like what you're seeing?</p>
            <p className="text-sm text-jow-cream/70">
              Order your own piece — we'll roll up to your door too.
            </p>
          </div>
          <Link to="/" className="btn-primary-jow bg-jow-gold text-jow-navy border-jow-gold">
            Shop JOW
          </Link>
        </div>

        <p className="text-[10px] text-jow-navy/40 text-center mt-8 uppercase tracking-widest">
          This link expires on {new Date(data.expires_at).toLocaleString()} · No personal details
          shared
        </p>
      </main>
    </PublicShell>
  );
}

function PublicShell({ children }) {
  return (
    <div className="min-h-screen bg-jow-cream/40">
      <header className="bg-white border-b border-jow-navy/10 px-4 sm:px-6 py-4 flex items-center gap-3">
        <img src={LOGO_URL} alt="JOW" className="h-8 w-8" />
        <div>
          <p className="font-display text-lg text-jow-navy leading-none">
            JewelleryOnWheels
          </p>
          <p className="label-overline text-jow-gold text-[9px] mt-1">
            Beauty that comes to you
          </p>
        </div>
      </header>
      {children}
    </div>
  );
}
